import { getPointsRuleAttention, isCustomPointsRule, type PointsRuleAttention } from '@/entities/expert-report';
import {
  findNestedKmPointsRuleAttention,
  kmGroupAsPointsRuleOwner,
  type KmAssessmentResponse,
  type KmOrderPosition,
  type KmStructureGroupNode,
  type KmStructureResponse,
} from '@/entities/expert-report-km';

/** Вид узла дерева структуры на модели знаний */
export type KmTreeNodeType = 'group' | 'assessment';

/** Узел дерева структуры заключения на модели знаний */
export type KmTreeNodeData = {
  /** Идентификатор узла в дереве: `kmgroup-{reportId}-{id}` либо `kmassessment-{reportId}-{id}` */
  id: string;
  label: string;
  type: KmTreeNodeType;
  reportId: number;
  /** Идентификатор экземпляра модели знаний — группы либо единицы оценивания */
  entityId: number;
  /** Структурная группа-заголовок */
  isHeader?: boolean;
  /** Результат группы считается особым правилом расчёта — не тем, что задано по умолчанию */
  hasCustomPointsRule?: boolean;
  /** Правило расчёта группы требует внимания и чем именно; `null` — не требует */
  pointsRuleAttention?: PointsRuleAttention | null;
  /** Правило расчёта требует внимания у одной из вложенных групп; `null` — не требует */
  nestedPointsRuleAttention?: PointsRuleAttention | null;
  children?: KmTreeNodeData[];
};

/** Идентификатор узла-заключения — контейнер верхнего уровня структуры (её корень) */
export const kmReportNodeId = (reportId: number): string => `report-${reportId}`;

/** Идентификатор узла-группы */
export const kmGroupNodeId = (reportId: number, groupId: number): string => `kmgroup-${reportId}-${groupId}`;

/** Идентификатор узла-единицы оценивания */
export const kmAssessmentNodeId = (reportId: number, assessmentId: number): string =>
  `kmassessment-${reportId}-${assessmentId}`;

/** Разбирает идентификатор узла дерева; для чужого идентификатора возвращает null */
export const parseKmNodeId = (nodeId: string): { type: KmTreeNodeType; entityId: number } | null => {
  const parts = nodeId.split('-');
  const entityId = Number(parts[2]);
  if (!Number.isFinite(entityId)) return null;
  if (parts[0] === 'kmgroup') return { type: 'group', entityId };
  if (parts[0] === 'kmassessment') return { type: 'assessment', entityId };
  return null;
};

const assessmentToTreeNode = (assessment: KmAssessmentResponse, reportId: number): KmTreeNodeData => ({
  id: kmAssessmentNodeId(reportId, assessment.id),
  label: assessment.name,
  type: 'assessment',
  reportId,
  entityId: assessment.id,
});

const groupToTreeNode = (group: KmStructureGroupNode, reportId: number): KmTreeNodeData => ({
  id: kmGroupNodeId(reportId, group.id),
  label: group.name,
  type: 'group',
  reportId,
  entityId: group.id,
  isHeader: group.isHeader,
  hasCustomPointsRule: isCustomPointsRule(group.pointsRule),
  pointsRuleAttention: getPointsRuleAttention(kmGroupAsPointsRuleOwner(group)),
  nestedPointsRuleAttention: findNestedKmPointsRuleAttention(group.children),
  // Внутри контейнера сначала идут вложенные группы, затем единицы оценивания:
  // у групп и у единиц свои независимые порядки, общего порядка между ними нет
  children: [
    ...group.children.map((child) => groupToTreeNode(child, reportId)),
    ...group.assessments.map((assessment) => assessmentToTreeNode(assessment, reportId)),
  ],
});

/** Собирает узлы верхнего уровня дерева структуры: группы корня и его единицы оценивания */
export const structureToTreeNodes = (structure: KmStructureResponse, reportId: number): KmTreeNodeData[] => [
  ...structure.groups.map((group) => groupToTreeNode(group, reportId)),
  ...structure.assessments.map((assessment) => assessmentToTreeNode(assessment, reportId)),
];

/** Есть ли узел с указанным идентификатором в поддереве */
export const hasKmDescendant = (nodes: KmTreeNodeData[], targetId: string): boolean => {
  for (const node of nodes) {
    if (node.id === targetId) return true;
    if (node.children && hasKmDescendant(node.children, targetId)) return true;
  }
  return false;
};

/**
 * Сгруппированное представление для хелпера `move` из @dnd-kit/helpers: ключ — идентификатор
 * контейнера (заключение либо группа), значение — упорядоченные идентификаторы его узлов.
 */
export const buildKmSortableGroups = (nodes: KmTreeNodeData[], rootNodeId: string): Record<string, string[]> => {
  const groups: Record<string, string[]> = {};
  const walk = (items: KmTreeNodeData[], containerId: string) => {
    groups[containerId] = items.map((item) => item.id);
    for (const item of items) {
      if (item.type === 'group') walk(item.children ?? [], item.id);
    }
  };
  walk(nodes, rootNodeId);
  return groups;
};

/** Идентификаторы всех потомков узла — чтобы запретить перенос группы внутрь самой себя */
export const collectKmSubtreeIds = (nodes: KmTreeNodeData[], rootId: string): Set<string> => {
  const result = new Set<string>();
  const gather = (items: KmTreeNodeData[]) => {
    for (const item of items) {
      result.add(item.id);
      if (item.children?.length) gather(item.children);
    }
  };
  const find = (items: KmTreeNodeData[]): boolean => {
    for (const item of items) {
      if (item.id === rootId) {
        gather(item.children ?? []);
        return true;
      }
      if (item.children?.length && find(item.children)) return true;
    }
    return false;
  };
  find(nodes);
  return result;
};

/** Куда встал узел после перетаскивания: контейнер и позиция среди узлов своего вида */
export type KmDropPlace = {
  /** Группа-контейнер; null — корень заключения */
  parentGroupId: number | null;
  /** Позиция среди групп либо среди единиц оценивания этого контейнера */
  orderPosition: KmOrderPosition;
};

/** Место первого узла в контейнере — им встаёт узел, вложенный в пустую группу */
export const FIRST_KM_ORDER_POSITION: KmOrderPosition = 1;

/**
 * Место вставки в ряду соседей по позиции: индекс смещён на одну, а позиция за границами ряда
 * прижимается к ближайшей.
 */
const insertionIndex = (orderPosition: KmOrderPosition, length: number): number =>
  Math.max(0, Math.min(orderPosition - 1, length));

/**
 * Определяет новое место узла по результату перестановки `move`. Позиция считается среди узлов
 * того же вида: у групп и у единиц оценивания контейнера свои независимые порядки, поэтому
 * общий индекс в смешанном списке серверу не годится.
 */
export const resolveKmDropPlace = (
  groups: Record<string, string[]>,
  nodeId: string,
  rootNodeId: string,
): KmDropPlace | null => {
  const containerId = Object.keys(groups).find((key) => groups[key].includes(nodeId));
  if (!containerId) return null;

  const moved = parseKmNodeId(nodeId);
  if (!moved) return null;

  const sameKind = groups[containerId].filter((id) => parseKmNodeId(id)?.type === moved.type);
  const index = sameKind.indexOf(nodeId);
  if (index < 0) return null;
  const orderPosition: KmOrderPosition = index + 1;

  if (containerId === rootNodeId) return { parentGroupId: null, orderPosition };

  const container = parseKmNodeId(containerId);
  // Единица оценивания контейнером быть не может — вложение в неё недопустимо
  if (!container || container.type !== 'group') return null;
  return { parentGroupId: container.entityId, orderPosition };
};

// --- Оптимистичное применение переноса к структуре ---

type KmContainerContent = {
  groups: KmStructureGroupNode[];
  assessments: KmAssessmentResponse[];
};

/** Возвращает содержимое контейнера структуры: корня (`null`) либо конкретной группы */
const readContainer = (structure: KmStructureResponse, groupId: number | null): KmContainerContent | null => {
  if (groupId === null) return { groups: structure.groups, assessments: structure.assessments };
  const find = (nodes: KmStructureGroupNode[]): KmStructureGroupNode | null => {
    for (const node of nodes) {
      if (node.id === groupId) return node;
      const found = find(node.children);
      if (found) return found;
    }
    return null;
  };
  const group = find(structure.groups);
  return group ? { groups: group.children, assessments: group.assessments } : null;
};

/** Пересобирает структуру, подменяя содержимое одного контейнера */
const writeContainer = (
  structure: KmStructureResponse,
  groupId: number | null,
  content: KmContainerContent,
): KmStructureResponse => {
  if (groupId === null) {
    return { ...structure, groups: content.groups, assessments: content.assessments };
  }
  const rewrite = (nodes: KmStructureGroupNode[]): KmStructureGroupNode[] =>
    nodes.map((node) =>
      node.id === groupId
        ? { ...node, children: content.groups, assessments: content.assessments }
        : { ...node, children: rewrite(node.children) },
    );
  return { ...structure, groups: rewrite(structure.groups) };
};

/** Извлекает группу из структуры вместе с её поддеревом */
const extractGroup = (
  structure: KmStructureResponse,
  groupId: number,
): { structure: KmStructureResponse; group: KmStructureGroupNode | null } => {
  let extracted: KmStructureGroupNode | null = null;
  const walk = (nodes: KmStructureGroupNode[]): KmStructureGroupNode[] => {
    const result: KmStructureGroupNode[] = [];
    for (const node of nodes) {
      if (node.id === groupId) {
        extracted = node;
        continue;
      }
      result.push({ ...node, children: walk(node.children) });
    }
    return result;
  };
  return { structure: { ...structure, groups: walk(structure.groups) }, group: extracted };
};

/** Извлекает единицу оценивания из структуры */
const extractAssessment = (
  structure: KmStructureResponse,
  assessmentId: number,
): { structure: KmStructureResponse; assessment: KmAssessmentResponse | null } => {
  let extracted: KmAssessmentResponse | null = null;
  const filter = (items: KmAssessmentResponse[]): KmAssessmentResponse[] =>
    items.filter((item) => {
      if (item.id !== assessmentId) return true;
      extracted = item;
      return false;
    });
  const walk = (nodes: KmStructureGroupNode[]): KmStructureGroupNode[] =>
    nodes.map((node) => ({ ...node, assessments: filter(node.assessments), children: walk(node.children) }));
  return {
    structure: { ...structure, assessments: filter(structure.assessments), groups: walk(structure.groups) },
    assessment: extracted,
  };
};

/**
 * Оптимистично переносит группу в другой контейнер структуры и на новую позицию.
 * Возвращает исходную структуру, если группу или контейнер найти не удалось.
 */
export const moveGroupInStructure = (
  structure: KmStructureResponse,
  groupId: number,
  place: KmDropPlace,
): KmStructureResponse => {
  const { structure: without, group } = extractGroup(structure, groupId);
  if (!group) return structure;

  const content = readContainer(without, place.parentGroupId);
  if (!content) return structure;

  const groups = [...content.groups];
  groups.splice(insertionIndex(place.orderPosition, groups.length), 0, group);
  return writeContainer(without, place.parentGroupId, {
    groups: groups.map((item, index) => ({ ...item, orderPosition: index + 1 })),
    assessments: content.assessments,
  });
};

/**
 * Оптимистично переносит единицу оценивания в другой контейнер структуры и на новую позицию.
 * Возвращает исходную структуру, если единицу или контейнер найти не удалось.
 */
export const moveAssessmentInStructure = (
  structure: KmStructureResponse,
  assessmentId: number,
  place: KmDropPlace,
): KmStructureResponse => {
  const { structure: without, assessment } = extractAssessment(structure, assessmentId);
  if (!assessment) return structure;

  const content = readContainer(without, place.parentGroupId);
  if (!content) return structure;

  const assessments = [...content.assessments];
  assessments.splice(insertionIndex(place.orderPosition, assessments.length), 0, {
    ...assessment,
    groupId: place.parentGroupId,
  });
  return writeContainer(without, place.parentGroupId, {
    groups: content.groups,
    assessments: assessments.map((item, index) => ({ ...item, orderPosition: index + 1 })),
  });
};
